import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaSpinner, FaExclamationTriangle, FaChevronDown, FaChevronUp, FaLock, FaEye } from 'react-icons/fa';
import { trackTimelineExpansion } from '../../../../utils/modernUITracking';
import { resolveEventType, dotClassKey, parseImpactReport } from '../../../../utils/timelineUtils';
import IncidentReportedImpacts from '../../IncidentDetails/IncidentReportedImpacts';
import styles from './ExpandedContent.module.css';
import dayjs from 'dayjs';

const ExpandedContent = ({
  incidentNumber,
  incidentTitle,
  data,
  loading,
  refreshing = false, // eslint-disable-line no-unused-vars
  lastUpdated = null, // eslint-disable-line no-unused-vars
  error,
  section = 'active_incidents',
  newrelic = null,
  user = null,
  onImpactCreated,
}) => {
  // Timeline expansion state
  const [isTimelineExpanded, setIsTimelineExpanded] = useState(false);

  // Determine viewport size and initial entries to show
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const MOBILE_INITIAL_ENTRIES = 5;
  const DESKTOP_INITIAL_ENTRIES = 5;
  const initialEntries = isMobile ? MOBILE_INITIAL_ENTRIES : DESKTOP_INITIAL_ENTRIES;

  // Scroll detection for shadow indicators
  const [hasScroll, setHasScroll] = useState(false);
  const [scrollPosition, setScrollPosition] = useState('top');
  const contentRef = useRef(null);

  useEffect(() => {
    const checkScroll = () => {
      if (!contentRef.current) return;
      const { scrollHeight, clientHeight } = contentRef.current;
      setHasScroll(scrollHeight > clientHeight);
    };
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [data]);

  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop === 0) setScrollPosition('top');
    else if (scrollTop + clientHeight >= scrollHeight - 5) setScrollPosition('bottom');
    else setScrollPosition('middle');
  }, []);

  if (loading) {
    return (
      <div className={styles.loadingState}>
        <FaSpinner className={styles.spinner} />
        <span>Loading details...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorState}>
        <FaExclamationTriangle />
        <span>Failed to load details. Please try again.</span>
      </div>
    );
  }

  if (!data) return null;

  const { statusDescription, statusFullDescription, affectedBrand, clientImpact, impactedWorkloads, updatedAt, updatedBy } = data;

  const updatedByName = updatedBy
    ? (typeof updatedBy === 'object' ? updatedBy.name : updatedBy)
    : null;

  const isPastIncidents = section === 'past_incidents';

  const SectionHeader = ({ title, testid }) => (
    <div className={styles.sectionHeader}>
      <h3 className={styles.sectionTitle} data-testid={testid}>{title}</h3>
    </div>
  );

  const statusText = (statusFullDescription || statusDescription || '').trim();
  const hasMeaningfulStatus = statusText.length > 0 && (incidentTitle ? statusText !== incidentTitle.trim() : true);

  return (
    <div
      ref={contentRef}
      className={`${styles.expandedContent} ${hasScroll ? styles.hasScroll : ''} ${styles[`scroll-${scrollPosition}`]}`}
      onScroll={handleScroll}
    >
      {/* Status Section — "Resolve Status" for past incidents, "Current Status" for active */}
      {hasMeaningfulStatus && (
        <section className={styles.section}>
          <SectionHeader title={isPastIncidents ? 'Resolve Status' : 'Current Status'} />
          <div className={styles.statusDetail}>
            <p className={styles.statusText}>{statusText}</p>
            {updatedAt && (
              <p className={styles.updateInfo}>
                Updated {dayjs(updatedAt).format('MMM D, YYYY h:mm A')}
                {updatedByName && ` by ${updatedByName}`}
              </p>
            )}
          </div>
        </section>
      )}

      {/* Client Impact — past incidents only */}
      {isPastIncidents && clientImpact && clientImpact.trim() && (
        <section className={styles.section}>
          <SectionHeader title="Client Impact" />
          <div className={styles.statusDetail}>
            <p className={styles.statusText}>{clientImpact.trim()}</p>
          </div>
        </section>
      )}

      {/* Affected Brand / Capability */}
      {affectedBrand && (
        <section className={styles.section}>
          <SectionHeader title="Affected Brand / Capability" testid="incident-affected-brand-heading" />
          <div className={styles.workloadsList}>
            {affectedBrand.split(', ').map((brand, index) => (
              <span key={index} className={styles.workloadBadge}>
                {brand.trim()}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Applications, Products or Services */}
      {impactedWorkloads && impactedWorkloads.length > 0 && (
        <section className={styles.section}>
          <SectionHeader title="Applications, Products or Services" testid="incident-applications-heading" />
          <div className={styles.workloadsList}>
            {impactedWorkloads.map((workload, index) => (
              <span key={index} className={`${styles.workloadBadge} ${styles[workload.status?.toLowerCase()]}`}>
                {workload.value || workload.label || workload.name || 'Unknown Service'}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Reported Impacts — active, non-resolved incidents only.
          Reuses IncidentReportedImpacts (manages its own modal, list, and polling).
          The wrapper stops click/keydown propagation so interacting with the
          Report Impact / Edit / Resolve / Re-open / Show-more controls does not
          collapse the parent IncidentCard (whose root is itself clickable).

          US1950081: a temporary problem STILL RENDERS this section; only the FETCH is
          skipped. The backend refuses impact reads on a temporary record
          (handler_impact.go:372), so `isTemporary` is passed down and the component
          declines to fetch. An earlier revision of this round gated the whole section
          on `!data.temporary` instead, which hid the heading and broke e2e MUD-02.
          Prop, not gate. `data.temporary` is mapped in
          Incidents/hooks/mapIncidentDetails.js. */}
      {section === 'active_incidents' && data.currentStatus !== 'Resolve' && (
        <section className={styles.section}>
          <div
            className={styles.reportedImpactsWrapper}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <IncidentReportedImpacts
              problemId={incidentNumber}
              user={user}
              onImpactCreated={onImpactCreated}
              isResolved={false}
              isTemporary={!!data.temporary}
            />
          </div>
        </section>
      )}

      {/* Timeline — active incidents only */}
      {!isPastIncidents && data.timelineEvents && data.timelineEvents.length > 0 && (
        <section className={styles.section}>
          <SectionHeader title="Timeline" testid="incident-timeline-heading" />
          <div
            className={`${styles.timelineContainer} ${isTimelineExpanded ? styles.timelineScrollable : ''}`}
          >
            {data.timelineEvents
              .slice(0, isTimelineExpanded ? data.timelineEvents.length : initialEntries)
              .map((event, index) => {
                const note = event.data || {};
                const resolvedType = resolveEventType(event);
                const authorName = event.eventType === 'workflow' ? null : (event.author?.name || 'Unknown');
                const rawMessage = note.message || note.content || note.description || note.title || note.body || 'No description';

                const parsed = resolvedType === 'impact-report'
                  ? parseImpactReport(rawMessage)
                  : null;

                const badges = [];
                if (event.eventType === 'communication') {
                  badges.push({ type: 'communication', label: 'Communication' });
                } else if (event.eventType === 'workflow') {
                  badges.push({ type: 'workflow', label: 'Workflow' });
                } else if (resolvedType === 'note') {
                  badges.push({ type: 'note', label: 'Note' });
                }
                if (parsed && parsed.isImpactReport) {
                  badges.push({ type: 'impact', label: 'Impact Report' });
                }
                if (note.private) {
                  badges.push({ type: 'private', label: 'Private', icon: <FaLock /> });
                }
                if (note.important) {
                  badges.push({ type: 'important', label: 'Important' });
                }
                if (note.pir) {
                  badges.push({ type: 'pir', label: 'PIR' });
                }
                if (note.public) {
                  badges.push({ type: 'public', label: 'Public', icon: <FaEye /> });
                }

                return (
                  <div key={event.id || index} className={styles.timelineEntry}>
                    <div className={[styles.timelineDot, styles[dotClassKey(resolvedType)]].filter(Boolean).join(' ')}></div>
                    <div className={styles.timelineContent}>
                      {badges.length > 0 && (
                        <div className={styles.timelineBadges}>
                          {badges.map((badge, i) => (
                            <span key={i} className={`${styles.timelineBadge} ${styles[badge.type]}`}>
                              {badge.icon && <span className={styles.badgeIcon}>{badge.icon}</span>}
                              {badge.label}
                            </span>
                          ))}
                        </div>
                      )}
                      {event.eventType === 'workflow' ? (
                        <div className={styles.timelineMessage}>
                          {note.workflow?.name || note.workflow?.label || rawMessage}
                        </div>
                      ) : event.eventType === 'communication' ? (
                        <div className={styles.timelineMessage}>
                          {note.subject || note.title || rawMessage}
                        </div>
                      ) : parsed && parsed.isImpactReport ? (
                        <div className={styles.compactMessage}>
                          <div className={styles.brandApp}>
                            {parsed.brand} - {parsed.application} • {parsed.status}
                          </div>
                        </div>
                      ) : (
                        <div className={styles.timelineMessage}>{rawMessage}</div>
                      )}
                      <div className={styles.timelineMeta}>
                        {authorName && (
                          <span className={styles.timelineAuthor}>{authorName}</span>
                        )}
                        {authorName && event.timestamp != null && (
                          <span className={styles.timelineSeparator}>•</span>
                        )}
                        {event.timestamp != null && (
                          <span className={styles.timelineTimestamp}>
                            {dayjs(event.timestamp).format('MMM D, YYYY h:mm A')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>

          {data.timelineEvents.length > initialEntries && (
            <button
              type="button"
              className={styles.showMoreButton}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const newExpandedState = !isTimelineExpanded;
                setIsTimelineExpanded(newExpandedState);
                trackTimelineExpansion(incidentNumber, {
                  totalEntries: data.timelineEvents.length,
                  visibleEntries: initialEntries,
                  expandedEntries: newExpandedState ? data.timelineEvents.length : initialEntries,
                  action: newExpandedState ? 'expand' : 'collapse',
                  section,
                }, newrelic);
              }}
              aria-expanded={isTimelineExpanded}
              aria-label={isTimelineExpanded ? 'Show fewer timeline entries' : `Show ${data.timelineEvents.length - initialEntries} more timeline entries`}
            >
              {isTimelineExpanded ? (
                <>
                  <FaChevronUp className={styles.showMoreIcon} />
                  <span>Show less</span>
                </>
              ) : (
                <>
                  <FaChevronDown className={styles.showMoreIcon} />
                  <span>Show {data.timelineEvents.length - initialEntries} more entries</span>
                </>
              )}
            </button>
          )}
        </section>
      )}
    </div>
  );
};

export default ExpandedContent;
